import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Loader2, Receipt, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatMinor } from "../lib/finance";
import { AGE_LABELS, billingError, totalsByCurrency } from "../lib/billing";
import { DocumentsList, InstallmentsList, PaymentsList } from "../components/billing/BillingTables";
import useBillingActions from "../components/billing/useBillingActions";
import BillingIndicators from "../components/billing/BillingIndicators";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TABS = [
  { id: "to_invoice", label: "À facturer", short: "À facturer" },
  { id: "overdue", label: "Impayés", short: "Impayés" },
  { id: "documents", label: "Factures et avoirs", short: "Factures" },
  { id: "payments", label: "Encaissements", short: "Encaissés" },
  { id: "indicators", label: "Indicateurs", short: "Indicateurs" },
];
const ALL = "__all__";
const PAGE_SIZE = 50;

const EMPTY = {
  to_invoice: ["Rien à facturer", "Les échéances des abonnements en cours apparaîtront ici à chaque période, jusqu'à ce que leur facture soit consignée."],
  overdue: ["Aucun impayé", "Une échéance dont la date limite est passée sans encaissement complet apparaît ici, avec son ancienneté."],
  documents: ["Aucune facture consignée", "Consignez ici les factures émises (numéro, date, montant, PDF) depuis une échéance à facturer."],
  payments: ["Aucun encaissement", "Les paiements reçus des pharmacies, consignés à la main, apparaîtront ici."],
  indicators: ["", ""],
};

export default function BillingInvoices() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get("tab")) ? params.get("tab") : "to_invoice";
  const age = Object.keys(AGE_LABELS).includes(params.get("age")) ? params.get("age") : "";
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ items: [], total: 0 });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);
  const setParam = (changes) => setParams((prev) => {
    const next = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v) next.set(k, v); else next.delete(k); }
    return next;
  }, { replace: true });

  const request = useMemo(() => {
    if (tab === "documents") return { path: "/billing/documents", params: { limit: PAGE_SIZE, ...(search ? { search } : {}) } };
    if (tab === "payments") return { path: "/billing/payments", params: { limit: PAGE_SIZE } };
    return { path: "/billing/installments", params: { limit: PAGE_SIZE, state: tab === "overdue" ? "overdue" : "not_invoiced", ...(tab === "overdue" && age ? { age } : {}) } };
  }, [tab, age, search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get(request.path, { params: request.params });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(billingError(e, "Impossible de charger cette liste."));
      setPhase("error");
    }
  }, [request]);
  useEffect(() => {
    if (tab === "indicators") return undefined;
    load();
    return () => { seq.current += 1; };
  }, [load, tab]);
  const rows = useBillingActions(user, load);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: body } = await api.get(request.path, { params: { ...request.params, skip: data.items.length } });
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      toast.error(billingError(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  const due = tab === "overdue" ? totalsByCurrency(data.items) : null;
  const [emptyTitle, emptyText] = EMPTY[tab];
  const visible = tab === "payments" && search
    ? data.items.filter((p) => `${p.pharmacy_name} ${p.reference} ${p.invoice_number || ""}`.toLowerCase().includes(search.toLowerCase()))
    : data.items;

  return (
    <div className="space-y-4" data-testid="billing-invoices-page">
      <div className="min-w-0">
        <h1 className="font-display font-bold text-2xl text-slate-900">Factures et échéances</h1>
        <p className="text-sm text-slate-500">Ce que les pharmacies doivent, ce qui est facturé et ce qui est encaissé. Une facture n'est pas un paiement.</p>
      </div>

      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Factures et échéances">
        {TABS.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} onClick={() => { setSearch(""); setParam({ tab: t.id === "to_invoice" ? "" : t.id, age: "" }); }}
            data-testid={`billing-invoices-tab-${t.id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${tab === t.id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            <span className="sm:hidden">{t.short}</span><span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {tab === "indicators" ? <BillingIndicators /> : (<>
      {(tab === "documents" || tab === "payments" || tab === "overdue") && (
        <div className="flex items-center gap-2 flex-wrap">
          {(tab === "documents" || tab === "payments") && (
            <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={tab === "documents" ? "Numéro ou pharmacie…" : "Pharmacie ou référence…"}
                className="pl-9 h-9" aria-label="Rechercher" data-testid="billing-invoices-search" />
            </div>
          )}
          {tab === "overdue" && (
            <Select value={age || ALL} onValueChange={(v) => setParam({ age: v === ALL ? "" : v })}>
              <SelectTrigger className="h-9 w-56" aria-label="Ancienneté du retard" data-testid="billing-overdue-age"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Toutes les anciennetés</SelectItem>
                {Object.entries(AGE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {due && Object.keys(due).length > 0 && (
            <span className="text-sm text-slate-600" data-testid="billing-overdue-total">
              Reste dû affiché : {Object.entries(due).map(([c, v]) => formatMinor(v, c)).join(" · ")}
            </span>
          )}
        </div>
      )}

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && visible.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="billing-invoices-empty">
          <Receipt className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">{emptyTitle}</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">{emptyText}</p>
        </Card>
      )}
      {phase === "ready" && visible.length > 0 && (
        <>
          <Card className="overflow-hidden" data-testid={`billing-invoices-list-${tab}`}>
            {tab === "documents" && <DocumentsList items={visible} actions={rows.documentActions} showPharmacy />}
            {tab === "payments" && <PaymentsList items={visible} actions={rows.paymentActions} showPharmacy />}
            {(tab === "to_invoice" || tab === "overdue") && (
              <InstallmentsList items={visible} actions={rows.installmentActions} showPharmacy
                primary={tab === "to_invoice" && rows.canInvoice ? (i) => (
                  <Button variant="outline" size="sm" className="h-8 mr-1" onClick={() => rows.openInvoice([i])} data-testid={`billing-invoice-${i.period_start}`}>Facturer</Button>
                ) : undefined} />
            )}
          </Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="billing-invoices-count">{data.items.length} sur {data.total}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus</Button>
            )}
          </div>
        </>
      )}
      </>)}
      {rows.dialogs}
    </div>
  );
}
